// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Mirrors the track's original children once for a gapless loop and animates with the Web Animations API.
// The mirror is rebuilt whenever the originals change, so edits never leave stale copies behind.
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });

    this.#observer = new MutationObserver((records) => {
      const touchesOriginals = records.some((r) => !r.target.closest?.("[data-marquee-clone]") &&
        ![...r.addedNodes, ...r.removedNodes].every((n) => n.nodeType === 1 && n.hasAttribute("data-marquee-clone")));
      if (touchesOriginals) this.#schedule();
    });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#animation?.cancel();
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setup());
  }

  #setup() {
    this.#observer?.disconnect();
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    // Drop any previous mirror, then copy the current originals so both halves always match.
    track.querySelectorAll(":scope > [data-marquee-clone]").forEach((n) => n.remove());

    if (!this.#mql.matches) {
      const clones = [...track.children].map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("data-marquee-clone", "");
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id");
        return clone;
      });
      track.append(...clones);
    }

    this.#observer?.observe(track, { childList: true, subtree: true, attributes: true, characterData: true });

    if (this.#mql.matches) return;

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
